/**
 * Startpunkt der Anwendung.
 *
 * Haelt den Zustand, schickt Aktionen durch den Regelkern und laesst Ansicht und
 * Oberflaeche darauf reagieren. Mehr passiert hier nicht - keine Regel, keine
 * Geometrie. Genau deshalb liesse sich der Zustand spaeter genauso gut von einem
 * Server bekommen.
 */
import './ui/stil.css'
import { anwenden, moeglicheAktionen, neuesSpiel, spieler } from './core/spiel'
import { STANDARDREGELN, type Aktion, type Zustand } from './core/typen'
import { botAktion } from './core/bots'
import { baueAnsicht } from './view/ansicht'
import { baueBuehne } from './view/szene'
import { baueOberflaeche, type Oberflaeche } from './ui/oberflaeche'
import { zeigeStart, type Aufstellung } from './ui/start'
import { hauptAktion } from './ui/beschriftung'
import { spieleKlang } from './ui/klang'
import { Vector3 } from 'three'
import { FELDER } from './core/data/spielplan'
import { EBENE } from './view/ebenen'

declare global {
  interface Window {
    __fuenfSterne?: {
      zustand: () => Zustand
      /** Die gerade waehlbaren Felder, leer wenn keine Feldwahl laeuft. */
      auswahl: () => number[]
      feldAufSchirm: (index: number) => { x: number; y: number } | null
    }
  }
}

/** Wartezeit zwischen zwei Bot-Aktionen, damit man mitlesen kann. */
const BOTTAKT = 620

const wurzel = document.getElementById('anwendung')!

zeigeStart(wurzel, starte)

function starte(auf: Aufstellung): void {
  let zustand: Zustand = neuesSpiel(auf.spieler, auf.saat, {
    ...STANDARDREGELN,
    rundenlimit: auf.rundenlimit,
  })
  let feldKandidaten: readonly Aktion[] | null = null
  let botZeit: number | null = null

  const flaeche: Oberflaeche = baueOberflaeche(wurzel, {
    waehle: (a) => fuehreAus(a),
    feldWahl: (kandidaten) => {
      feldKandidaten = kandidaten
      ansicht.zeigeAuswahl(kandidaten.map((k) => ('feld' in k ? k.feld : -1)))
      flaeche.hinweis('Feld auf dem Brett wählen - Esc bricht ab')
    },
    neuStarten: () => {
      if (botZeit !== null) window.clearTimeout(botZeit)
      zeigeStart(wurzel, starte)
    },
  })

  const buehne = baueBuehne(flaeche.buehne)
  const ansicht = baueAnsicht()
  buehne.szene.add(ansicht.wurzel)

  function beendeFeldwahl(): void {
    feldKandidaten = null
    ansicht.zeigeAuswahl([])
    flaeche.hinweis(null)
  }

  function fuehreAus(a: Aktion): void {
    beendeFeldwahl()
    schritt(a)
  }

  /** Eine Aktion anwenden und den passenden Klang dazu spielen. */
  function schritt(a: Aktion): void {
    const vorher = zustand
    zustand = anwenden(zustand, a)
    klangZu(vorher, zustand, a)
    aktualisiere()
  }

  function klangZu(vorher: Zustand, nachher: Zustand, a: Aktion): void {
    if (nachher.phase === 'ende' && vorher.phase !== 'ende') {
      spieleKlang('sieg')
      return
    }
    const neuRaus = nachher.spieler.some(
      (s, i) => s.ausgeschieden && !vorher.spieler[i]!.ausgeschieden,
    )
    if (neuRaus) {
      spieleKlang('ausgeschieden')
      return
    }
    const gebaut = Object.values(nachher.besitz).reduce(
      (n, b) => n + (b?.gebaeude ?? 0) + (b?.freizeitpark ? 1 : 0),
      0,
    )
    const vorherGebaut = Object.values(vorher.besitz).reduce(
      (n, b) => n + (b?.gebaeude ?? 0) + (b?.freizeitpark ? 1 : 0),
      0,
    )
    if (gebaut > vorherGebaut) {
      spieleKlang('bauen')
      return
    }
    switch (a.art) {
      case 'wuerfeln':
        spieleKlang('wuerfel')
        break
      case 'uebernachten':
      case 'grundstueck-kaufen':
      case 'zwangskauf':
        spieleKlang('muenzen')
        break
      case 'eingang-kaufen':
      case 'gratis-eingang':
        spieleKlang('eingang')
        break
      case 'bieten':
        spieleKlang('gebot')
        break
      default:
        break
    }
  }

  function aktualisiere(): void {
    ansicht.zeige(zustand)
    ansicht.markiereFeld(zustand.phase === 'ende' ? null : spieler(zustand, zustand.amZug).feld)
    flaeche.zeichne(zustand)
    planeBot()
  }

  function amDrueckerId(): number {
    return zustand.phase === 'auktion' && zustand.auktion
      ? zustand.auktion.amZug
      : zustand.amZug
  }

  function planeBot(): void {
    if (botZeit !== null) window.clearTimeout(botZeit)
    botZeit = null
    if (zustand.phase === 'ende') return
    if (!spieler(zustand, amDrueckerId()).bot) return
    botZeit = window.setTimeout(() => {
      // Erst ziehen lassen, dann handeln: solange das Auto noch faehrt oder ein
      // Gebaeude waechst, wartet der Bot.
      if (!ansicht.ruht()) {
        planeBot()
        return
      }
      schritt(botAktion(zustand))
    }, BOTTAKT)
  }

  buehne.renderer.domElement.addEventListener('click', (e) => {
    if (!feldKandidaten) return
    const r = buehne.renderer.domElement.getBoundingClientRect()
    const x = ((e.clientX - r.left) / r.width) * 2 - 1
    const y = -((e.clientY - r.top) / r.height) * 2 + 1
    const feld = ansicht.feldUnter(buehne.kamera, x, y)
    if (feld === null) return
    const treffer = feldKandidaten.find((k) => 'feld' in k && k.feld === feld)
    if (treffer) fuehreAus(treffer)
  })

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (flaeche.regelnOffen()) flaeche.regelnUmschalten()
      else beendeFeldwahl()
      return
    }
    if (e.key === 'r' || e.key === 'R') {
      if (e.target instanceof HTMLInputElement) return
      flaeche.regelnUmschalten()
      return
    }
    if (e.key !== ' ' && e.key !== 'Enter') return
    if (e.target instanceof HTMLInputElement) return
    if (flaeche.regelnOffen()) return
    if (zustand.phase === 'ende') return
    if (spieler(zustand, amDrueckerId()).bot) return
    const naechste = hauptAktion(zustand, moeglicheAktionen(zustand))
    if (!naechste) return
    e.preventDefault()
    fuehreAus(naechste)
  })

  window.addEventListener('resize', () => buehne.passeAn())

  // Ein kleiner, nur lesender Haken fuer den automatisierten Probelauf: er braucht
  // die Bildschirmkoordinate eines Feldes, um einen Eingang wirklich anzuklicken.
  // Ohne das liesse sich der wichtigste Bedienweg des Spiels nicht pruefen.
  window.__fuenfSterne = {
    zustand: () => zustand,
    auswahl: () =>
      (feldKandidaten ?? [])
        .map((k) => ('feld' in k ? k.feld : -1))
        .filter((f) => f >= 0),
    feldAufSchirm: (index: number) => {
      const feld = FELDER[index]
      if (!feld) return null
      const v = new Vector3(feld.mitte.x, EBENE.symbol, feld.mitte.z).project(buehne.kamera)
      const r = buehne.renderer.domElement.getBoundingClientRect()
      return {
        x: r.left + ((v.x + 1) / 2) * r.width,
        y: r.top + ((1 - v.y) / 2) * r.height,
      }
    },
  }

  let vorherigeZeit = performance.now()
  function bild(jetzt: number): void {
    const dt = Math.min(0.1, (jetzt - vorherigeZeit) / 1000)
    vorherigeZeit = jetzt
    ansicht.aktualisiere(dt)
    buehne.zeichne()
    requestAnimationFrame(bild)
  }
  buehne.passeAn()
  aktualisiere()
  requestAnimationFrame(bild)
}
